<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { createConsumer } from '@rails/actioncable';
import { debounce } from '@chatwoot/utils';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import PdvSalesReportsAPI from 'dashboard/api/pdvSalesReports';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { format } from 'date-fns';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { accountId, accountScopedRoute } = useAccount();
const { isAdmin } = useAdmin();
const currentUser = computed(() => store.getters.getCurrentUser);

const sales = ref([]);
const meta = ref({ total_count: 0, total_pages: 1, current_page: 1 });
const isLoading = ref(false);
const showFilters = ref(false);

const filters = reactive({
  search: route.query.search || '',
  status: route.query.status || '',
  agentId: route.query.agent_id ? Number(route.query.agent_id) : null,
  dateFrom: route.query.date_from || '',
  dateTo: route.query.date_to || '',
});

const pagination = reactive({
  page: Number(route.query.page) || 1,
  perPage: Number(route.query.per_page) || 25,
});

const statusOptions = [
  { value: '', label: t('PDV_SALES_LIST.STATUS.ALL') },
  { value: 'pending', label: t('PDV_SALES_LIST.STATUS.PENDING') },
  { value: 'sent', label: t('PDV_SALES_LIST.STATUS.SENT') },
  { value: 'failed', label: t('PDV_SALES_LIST.STATUS.FAILED') },
  { value: 'registered', label: t('PDV_SALES_LIST.STATUS.REGISTERED') },
];

const statusClass = status => {
  const map = {
    pending: 'bg-n-slate-3 text-n-slate-11 border-n-slate-6',
    sent: 'bg-n-amber-3 text-n-amber-12 border-n-amber-6',
    failed: 'bg-n-ruby-4 text-n-ruby-12 border-n-ruby-8',
    registered: 'bg-n-teal-3 text-n-teal-12 border-n-teal-6',
  };
  return map[status] || map.pending;
};

const statusLabel = status =>
  t(`PDV_SALES_LIST.STATUS.${(status || 'pending').toUpperCase()}`);

const agents = useMapGetter('agents/getAgents');

const hasActiveFilters = computed(() =>
  Boolean(
    filters.status || filters.agentId || filters.dateFrom || filters.dateTo
  )
);

const toTimestamp = dateStr => {
  if (!dateStr) return null;
  const parsed = Date.parse(dateStr);
  return Number.isNaN(parsed) ? null : Math.floor(parsed / 1000);
};

const buildParams = () => ({
  since: toTimestamp(filters.dateFrom),
  until: toTimestamp(filters.dateTo),
  agentId: filters.agentId,
  status: filters.status || undefined,
  q: filters.search || undefined,
  page: pagination.page,
  perPage: pagination.perPage,
});

function updateRouteQuery() {
  const query = {
    page: pagination.page,
    per_page: pagination.perPage,
  };
  if (filters.search) query.search = filters.search;
  if (filters.status) query.status = filters.status;
  if (filters.agentId) query.agent_id = filters.agentId;
  if (filters.dateFrom) query.date_from = filters.dateFrom;
  if (filters.dateTo) query.date_to = filters.dateTo;
  router.replace({ query });
}

async function fetchSales() {
  isLoading.value = true;
  try {
    const { data } = await PdvSalesReportsAPI.getSales(buildParams());
    sales.value = data.data || [];
    meta.value = data.meta || {
      total_count: 0,
      total_pages: 1,
      current_page: 1,
    };
  } catch (error) {
    useAlert(t('PDV_SALES_LIST.ERRORS.FETCH'));
  } finally {
    isLoading.value = false;
  }
}

const openFilters = () => {
  showFilters.value = true;
};
const closeFilters = () => {
  showFilters.value = false;
};

const applyFilters = () => {
  pagination.page = 1;
  updateRouteQuery();
  fetchSales();
  closeFilters();
};

const resetFilters = () => {
  filters.search = '';
  filters.status = '';
  filters.agentId = null;
  filters.dateFrom = '';
  filters.dateTo = '';
  applyFilters();
};

const totalCount = computed(() => meta.value?.total_count || 0);
const totalPages = computed(() => meta.value?.total_pages || 1);
const hasResults = computed(() => totalCount.value > 0);

const goToPage = page => {
  const maxPages = totalPages.value;
  if (page < 1 || page > maxPages) return;
  pagination.page = page;
  updateRouteQuery();
  fetchSales();
};

const searchHandler = debounce(() => {
  pagination.page = 1;
  updateRouteQuery();
  fetchSales();
}, 300);

const handleSearchInput = value => {
  filters.search = value;
  searchHandler();
};

const formatCurrency = val =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    val || 0
  );

const formatDate = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'MMM d, yyyy h:mm a');
};

const goBackToReport = () => {
  router.push(accountScopedRoute('pdv_sales_reports'));
};

const expandedSaleId = ref(null);
const toggleCard = id => {
  expandedSaleId.value = expandedSaleId.value === id ? null : id;
};
const isExpanded = id => expandedSaleId.value === id;

const itemOriginalPrice = item => (item.unit_price || 0) + (item.discount || 0);

const conversationLink = sale => {
  if (!sale.conversation_id || !accountId.value) return '';
  return frontendURL(
    `accounts/${accountId.value}/conversations/${sale.conversation_id}`
  );
};

const openConversation = sale => {
  const link = conversationLink(sale);
  if (link) router.push(link);
};

const cableURL = () => {
  const { websocketURL = '' } = window.chatwootConfig || {};
  return websocketURL ? `${websocketURL}/cable` : '/cable';
};

let cable = null;

onMounted(() => {
  if (isAdmin.value) {
    store.dispatch('agents/get');
  }
  fetchSales();

  cable = createConsumer(cableURL());
  cable.subscriptions.create(
    {
      channel: 'PdvSalesChannel',
      account_id: accountId.value,
      pubsub_token: currentUser.value?.pubsub_token,
    },
    {
      received({ event }) {
        if (event === 'sale_consumed') fetchSales();
      },
    }
  );
});

onUnmounted(() => {
  cable?.disconnect();
  cable = null;
});
</script>

<template>
  <div class="flex flex-col h-full w-full">
    <header
      class="sticky top-0 z-10 border-b border-n-weak/80 bg-n-background/95 backdrop-blur"
    >
      <div class="mx-auto flex w-full max-w-[60rem] flex-col gap-4 px-6 py-4">
        <NextButton
          ghost
          slate
          size="sm"
          icon="i-lucide-arrow-left"
          class="self-start"
          :label="t('PDV_SALES_LIST.BACK_TO_REPORT')"
          @click="goBackToReport"
        />
        <div
          class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex flex-col gap-1">
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ t('PDV_SALES_LIST.TITLE') }}
            </h1>
            <p class="text-sm text-n-slate-11">
              {{ t('PDV_SALES_LIST.DESCRIPTION') }}
            </p>
          </div>
          <div
            class="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center"
          >
            <Input
              class="w-full sm:w-72"
              :model-value="filters.search"
              type="search"
              :placeholder="t('PDV_SALES_LIST.FILTERS.SEARCH_PLACEHOLDER')"
              :custom-input-class="[
                'h-10 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-9 rtl:!pr-9',
              ]"
              @update:model-value="handleSearchInput"
            >
              <template #prefix>
                <span
                  class="absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-11 i-lucide-search ltr:left-3 rtl:right-3"
                />
              </template>
            </Input>
            <NextButton
              ghost
              slate
              size="sm"
              :icon="showFilters ? 'i-lucide-filter-x' : 'i-lucide-list-filter'"
              @click="openFilters"
            >
              <span class="inline-flex items-center gap-2">
                {{
                  showFilters
                    ? t('PDV_SALES_LIST.FILTERS.HIDE_PANEL')
                    : t('PDV_SALES_LIST.FILTERS.SHOW_PANEL')
                }}
                <span
                  v-if="hasActiveFilters"
                  class="h-2 w-2 rounded-full bg-n-brand"
                />
              </span>
            </NextButton>
          </div>
        </div>
      </div>
    </header>

    <transition name="fade">
      <div
        v-if="showFilters"
        class="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
        @click="closeFilters"
      >
        <div
          class="w-full max-w-3xl rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl"
          @click.stop
        >
          <header
            class="flex items-center justify-between border-b border-n-weak px-6 py-4"
          >
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{ t('PDV_SALES_LIST.FILTERS.TITLE') }}
              </h2>
              <p class="text-sm text-n-slate-11">
                {{ t('PDV_SALES_LIST.FILTERS.SUBTITLE') }}
              </p>
            </div>
            <button
              class="rounded-full p-2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-solid-3 transition-colors"
              @click="closeFilters"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </header>
          <div class="max-h-[65vh] overflow-y-auto px-6 py-5">
            <div class="grid gap-5 md:grid-cols-2">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold uppercase text-n-slate-10">
                  {{ t('PDV_SALES_LIST.FILTERS.STATUS') }}
                </label>
                <select
                  v-model="filters.status"
                  class="rounded-xl border border-n-weak bg-transparent px-3 py-2 text-sm focus:border-n-brand focus:outline-none"
                >
                  <option
                    v-for="status in statusOptions"
                    :key="status.value || 'all'"
                    :value="status.value"
                  >
                    {{ status.label }}
                  </option>
                </select>
              </div>
              <div v-if="isAdmin" class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold uppercase text-n-slate-10">
                  {{ t('PDV_SALES_LIST.FILTERS.AGENT') }}
                </label>
                <select
                  v-model="filters.agentId"
                  class="rounded-xl border border-n-weak bg-transparent px-3 py-2 text-sm focus:border-n-brand focus:outline-none"
                >
                  <option :value="null">—</option>
                  <option
                    v-for="agent in agents"
                    :key="agent.id"
                    :value="agent.id"
                  >
                    {{ agent.name }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold uppercase text-n-slate-10">
                  {{ t('PDV_SALES_LIST.FILTERS.DATE_FROM') }}
                </label>
                <input
                  v-model="filters.dateFrom"
                  class="rounded-xl border border-n-weak bg-transparent px-3 py-2 text-sm focus:border-n-brand focus:outline-none"
                  type="date"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold uppercase text-n-slate-10">
                  {{ t('PDV_SALES_LIST.FILTERS.DATE_TO') }}
                </label>
                <input
                  v-model="filters.dateTo"
                  class="rounded-xl border border-n-weak bg-transparent px-3 py-2 text-sm focus:border-n-brand focus:outline-none"
                  type="date"
                />
              </div>
            </div>
          </div>
          <footer
            class="flex flex-wrap items-center justify-end gap-3 border-t border-n-weak px-6 py-4"
          >
            <NextButton
              faded
              slate
              :label="t('PDV_SALES_LIST.FILTERS.RESET')"
              @click="resetFilters"
            />
            <NextButton
              :label="t('PDV_SALES_LIST.FILTERS.APPLY')"
              @click="applyFilters"
            />
          </footer>
        </div>
      </div>
    </transition>

    <section class="flex-1 overflow-auto">
      <div v-if="isLoading" class="flex h-full items-center justify-center">
        <Spinner />
      </div>
      <div
        v-else-if="!hasResults"
        class="flex h-full flex-col items-center justify-center gap-2"
      >
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ t('PDV_SALES_LIST.EMPTY.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('PDV_SALES_LIST.EMPTY.DESCRIPTION') }}
        </p>
      </div>
      <div v-else class="px-6 py-4">
        <div class="mx-auto flex w-full max-w-[60rem] flex-col gap-4">
          <CardLayout v-for="sale in sales" :key="sale.id">
            <div class="flex w-full flex-col gap-4">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div class="flex min-w-0 flex-1 items-center gap-3">
                  <Avatar
                    :name="sale.contact?.name || '—'"
                    :src="sale.contact?.thumbnail || ''"
                    :size="40"
                    rounded-full
                  />
                  <div class="min-w-0 flex flex-1 flex-col gap-0.5">
                    <p class="truncate text-base font-semibold text-n-slate-12">
                      {{ sale.contact?.name || '—' }}
                    </p>
                    <p class="truncate text-sm text-n-slate-11">
                      {{ sale.sale_code }} · {{ formatDate(sale.sale_date) }}
                    </p>
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <span
                    class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium"
                    :class="statusClass(sale.status)"
                  >
                    {{ statusLabel(sale.status) }}
                  </span>
                  <NextButton
                    icon="i-lucide-chevron-down"
                    color="slate"
                    ghost
                    size="xs"
                    class="transition-transform"
                    :class="{ 'rotate-180': isExpanded(sale.id) }"
                    @click.stop="toggleCard(sale.id)"
                  />
                </div>
              </div>

              <div
                class="grid gap-4 text-sm text-n-slate-12 md:grid-cols-2 lg:grid-cols-4"
              >
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-semibold uppercase text-n-slate-10">
                    {{ t('PDV_SALES_LIST.TABLE.AGENT') }}
                  </span>
                  <span>{{ sale.agent?.name || '—' }}</span>
                </div>
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-semibold uppercase text-n-slate-10">
                    {{ t('PDV_SALES_LIST.TABLE.NET_AMOUNT') }}
                  </span>
                  <span>{{ formatCurrency(sale.net_amount) }}</span>
                </div>
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-semibold uppercase text-n-slate-10">
                    {{ t('PDV_SALES_LIST.TABLE.DISCOUNT_AMOUNT') }}
                  </span>
                  <span>{{ formatCurrency(sale.discount_amount) }}</span>
                </div>
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-semibold uppercase text-n-slate-10">
                    {{ t('PDV_SALES_LIST.TABLE.PAYMENT_METHOD') }}
                  </span>
                  <span>{{ sale.payment_method || '—' }}</span>
                </div>
              </div>

              <div
                v-if="sale.status === 'registered' || sale.status === 'failed'"
                class="border-t border-n-weak pt-3 text-sm"
              >
                <span
                  v-if="sale.status === 'registered'"
                  class="text-n-slate-11"
                >
                  {{ t('PDV_SALES_LIST.TABLE.CONSUMED_AT') }}:
                  {{ formatDate(sale.consumed_at) }}
                  <template v-if="sale.consumed_by">
                    ({{ sale.consumed_by }})
                  </template>
                </span>
                <span v-else class="text-n-ruby-11">
                  {{ t('PDV_SALES_LIST.TABLE.ERROR') }}:
                  {{ sale.integrahub_error || '—' }}
                </span>
              </div>

              <transition name="fade">
                <div
                  v-if="isExpanded(sale.id)"
                  class="flex flex-col gap-4 border-t border-n-weak pt-4"
                >
                  <div
                    class="flex flex-col gap-4 rounded-xl bg-n-slate-3 dark:bg-n-solid-3 p-4"
                  >
                    <h4 class="text-xs font-semibold uppercase text-n-slate-10">
                      {{ t('PDV_SALES_LIST.DETAILS.ITEMS_TITLE') }}
                    </h4>
                    <div
                      v-for="(item, index) in sale.items || []"
                      :key="`${sale.id}-${item.product_id}-${index}`"
                      class="flex flex-col gap-3 border-b border-n-weak/60 pb-4 last:border-0 last:pb-0"
                    >
                      <div class="flex items-center gap-3">
                        <div
                          class="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-n-solid-1"
                        >
                          <img
                            v-if="item.image_url"
                            :src="item.image_url"
                            :alt="item.name"
                            class="size-full object-cover"
                          />
                          <span
                            v-else
                            class="i-lucide-package size-5 text-n-slate-10"
                          />
                        </div>
                        <p class="text-sm font-medium text-n-slate-12">
                          {{ item.name || '—' }}
                        </p>
                      </div>
                      <div
                        class="grid gap-4 text-sm text-n-slate-12 md:grid-cols-2 lg:grid-cols-4"
                      >
                        <div class="flex flex-col gap-1">
                          <span
                            class="text-xs font-semibold uppercase text-n-slate-10"
                          >
                            {{ t('PDV_SALES_LIST.DETAILS.SKU') }}
                          </span>
                          <span>{{ item.sku || '—' }}</span>
                        </div>
                        <div class="flex flex-col gap-1">
                          <span
                            class="text-xs font-semibold uppercase text-n-slate-10"
                          >
                            {{ t('PDV_SALES_LIST.DETAILS.QTY') }}
                          </span>
                          <span>{{ item.qty }}</span>
                        </div>
                        <div class="flex flex-col gap-1">
                          <span
                            class="text-xs font-semibold uppercase text-n-slate-10"
                          >
                            {{ t('PDV_SALES_LIST.DETAILS.UNIT_PRICE') }}
                          </span>
                          <span>{{
                            formatCurrency(itemOriginalPrice(item))
                          }}</span>
                        </div>
                        <div class="flex flex-col gap-1">
                          <span
                            class="text-xs font-semibold uppercase text-n-slate-10"
                          >
                            {{ t('PDV_SALES_LIST.DETAILS.ITEM_DISCOUNT') }}
                          </span>
                          <span>{{
                            item.discount ? formatCurrency(item.discount) : '—'
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="grid gap-3 border-t border-n-weak pt-4 text-sm text-n-slate-12 md:grid-cols-2 lg:grid-cols-3"
                  >
                    <div class="flex flex-col gap-1">
                      <span
                        class="text-xs font-semibold uppercase text-n-slate-10"
                      >
                        {{ t('PDV_SALES_LIST.DETAILS.SALE_ID') }}
                      </span>
                      <span>{{ sale.id }}</span>
                    </div>
                    <div class="flex flex-col gap-1">
                      <span
                        class="text-xs font-semibold uppercase text-n-slate-10"
                      >
                        {{ t('PDV_SALES_LIST.DETAILS.INTEGRAHUB_STATUS') }}
                      </span>
                      <span>{{ sale.integrahub_status || '—' }}</span>
                    </div>
                    <div
                      v-if="sale.conversation_id"
                      class="flex flex-col gap-1"
                    >
                      <span
                        class="text-xs font-semibold uppercase text-n-slate-10"
                      >
                        {{ t('PDV_SALES_LIST.DETAILS.CONVERSATION') }}
                      </span>
                      <NextButton
                        size="sm"
                        variant="link"
                        class="!px-0 justify-start"
                        :label="t('PDV_SALES_LIST.DETAILS.OPEN_CONVERSATION')"
                        @click="openConversation(sale)"
                      />
                    </div>
                    <div v-if="sale.consumed_at" class="flex flex-col gap-1">
                      <span
                        class="text-xs font-semibold uppercase text-n-slate-10"
                      >
                        {{ t('PDV_SALES_LIST.DETAILS.CONSUMED_AT') }}
                      </span>
                      <span>{{ formatDate(sale.consumed_at) }}</span>
                    </div>
                    <div v-if="sale.consumed_by" class="flex flex-col gap-1">
                      <span
                        class="text-xs font-semibold uppercase text-n-slate-10"
                      >
                        {{ t('PDV_SALES_LIST.DETAILS.CONSUMED_BY') }}
                      </span>
                      <span>{{ sale.consumed_by }}</span>
                    </div>
                    <div class="flex flex-col gap-1">
                      <span
                        class="text-xs font-semibold uppercase text-n-slate-10"
                      >
                        {{ t('PDV_SALES_LIST.DETAILS.CREATED_AT') }}
                      </span>
                      <span>{{ formatDate(sale.created_at) }}</span>
                    </div>
                    <div class="flex flex-col gap-1">
                      <span
                        class="text-xs font-semibold uppercase text-n-slate-10"
                      >
                        {{ t('PDV_SALES_LIST.DETAILS.UPDATED_AT') }}
                      </span>
                      <span>{{ formatDate(sale.updated_at) }}</span>
                    </div>
                    <div v-if="sale.contact?.email" class="flex flex-col gap-1">
                      <span
                        class="text-xs font-semibold uppercase text-n-slate-10"
                      >
                        {{ t('PDV_SALES_LIST.DETAILS.CUSTOMER_EMAIL') }}
                      </span>
                      <span>{{ sale.contact.email }}</span>
                    </div>
                    <div
                      v-if="sale.contact?.phone_number"
                      class="flex flex-col gap-1"
                    >
                      <span
                        class="text-xs font-semibold uppercase text-n-slate-10"
                      >
                        {{ t('PDV_SALES_LIST.DETAILS.CUSTOMER_PHONE') }}
                      </span>
                      <span>{{ sale.contact.phone_number }}</span>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </CardLayout>
        </div>
      </div>
    </section>

    <footer
      v-if="hasResults"
      class="flex items-center justify-between border-t border-n-weak px-6 py-3 text-sm"
    >
      <span class="text-n-slate-11">
        {{ totalCount }}
        {{ totalCount === 1 ? 'item' : 'items' }}
      </span>
      <div class="flex items-center gap-2">
        <NextButton
          faded
          slate
          size="sm"
          :disabled="pagination.page === 1"
          icon="i-lucide-chevron-left"
          @click="goToPage(pagination.page - 1)"
        />
        <span>{{ pagination.page }} / {{ totalPages }}</span>
        <NextButton
          faded
          slate
          size="sm"
          :disabled="pagination.page >= totalPages"
          icon="i-lucide-chevron-right"
          @click="goToPage(pagination.page + 1)"
        />
      </div>
    </footer>
  </div>
</template>
